/**
 * LeaderboardPage — today's scores, paginated.
 *
 * Pulls /api/leaderboard/today on mount with default limit=100 (top 100).
 * "Load more" calls again with offset advancing by the page size. The
 * server tracks hasMore against a Redis-cached totalPlayers count so the
 * button hides precisely when we run out.
 *
 * Country toggle (Global ↔ <my country>) flips the filter and refetches
 * from offset=0. The user's own country comes from their profile; if
 * they haven't picked one yet ("XX"), the toggle is hidden.
 *
 * Anonymous users see their auto-generated "Striker #ABCDEF" name. Authed
 * users see their email or chosen displayName, with a Pro pill.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ApiError,
  api,
  type LeaderboardResponse,
  type LeaderboardRow,
} from "@/api/client";
import { useAuthStore } from "@/store/authStore";
import { flagUrl, resolveCountry } from "@/lib/countries";
import { puzzleLabel } from "@/lib/puzzleNumber";

const PAGE_SIZE = 100;

function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.status}: ${err.message}`;
  if (err instanceof Error) return err.message;
  return "Unknown error";
}

/**
 * Country chip — flag PNG + 2-letter ISO code, with the full country name
 * on hover. Uses flagcdn.com PNGs (cross-platform; Windows doesn't render
 * regional-indicator emoji as flags). XX (unset) falls back to a globe.
 */
function CountryChip({ code }: { code: string }) {
  const url = flagUrl(code, 40);
  const name = resolveCountry(code).name;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md bg-white/5 px-1.5 py-0.5"
      title={name}
    >
      {url ? (
        <img
          src={url}
          width={18}
          height={13}
          loading="lazy"
          decoding="async"
          alt=""
          className="rounded-[2px] shadow-[0_0_0_1px_rgba(255,255,255,0.15)]"
        />
      ) : (
        <span aria-hidden="true" className="text-sm leading-none">🌐</span>
      )}
      <span className="text-[10px] font-semibold uppercase tracking-wider opacity-70">
        {code === "XX" ? "—" : code.toUpperCase()}
      </span>
    </span>
  );
}

function Row({ r }: { r: LeaderboardRow }) {
  const { t } = useTranslation();
  const base = "flex items-center gap-3 rounded-xl px-3 py-2 text-sm";
  const highlight = r.isMe ? "bg-emerald-700/40 ring-1 ring-emerald-300/40" : "hover:bg-white/5";
  return (
    <div className={`${base} ${highlight}`}>
      <span className="w-10 text-right tabular-nums opacity-60">{r.rank}</span>
      <CountryChip code={r.countryCode} />
      <span className="flex-1 truncate">
        {r.displayName}
        {r.founderNumber != null ? (
          <span
            className="ml-2 rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-950"
            title={t("founder.badge_tooltip", { number: r.founderNumber })}
          >
            {t("founder.badge_short")}
          </span>
        ) : r.isPro ? (
          <span className="ml-2 rounded-full bg-amber-300/90 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-950">
            Pro
          </span>
        ) : null}
        {r.isMe && (
          <span className="ml-2 rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] font-bold uppercase">
            You
          </span>
        )}
      </span>
      <span className="font-semibold tabular-nums">{r.score}</span>
    </div>
  );
}

export function LeaderboardPage() {
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterCountry, setFilterCountry] = useState<string | null>(null);
  const userCountry = useAuthStore((s) => s.user?.countryCode ?? null);
  const showCountryToggle = !!userCountry && userCountry !== "XX";

  // Initial load + refresh on filter change. We reset the rows list each
  // time the filter flips because rank numbers belong to the active scope
  // (global vs country) and shouldn't be mixed.
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setRows([]);
    setError(null);
    setNeedsSignIn(false);
    api
      .leaderboardToday({
        offset: 0,
        limit: PAGE_SIZE,
        ...(filterCountry ? { country: filterCountry } : {}),
      })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setRows(d.rows);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          setNeedsSignIn(true);
          return;
        }
        setError(describeError(err));
      });
    return () => { cancelled = true; };
  }, [filterCountry]);

  const loadMore = async () => {
    if (loadingMore || !data || !data.hasMore) return;
    setLoadingMore(true);
    try {
      const next = await api.leaderboardToday({
        offset: rows.length,
        limit: PAGE_SIZE,
        ...(filterCountry ? { country: filterCountry } : {}),
      });
      setRows((prev) => [...prev, ...next.rows]);
      setData(next); // updated totalPlayers + hasMore for the next click
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-full w-[min(36rem,100%)] flex-col gap-4 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Today&rsquo;s leaderboard</h1>
          {data && (
            <p className="text-xs opacity-60">
              {puzzleLabel(data.puzzleDate)} &middot; {data.totalPlayers.toLocaleString()}{" "}
              {data.totalPlayers === 1 ? "player" : "players"}
              {data.country && (
                <> &middot; {resolveCountry(data.country).name}</>
              )}
            </p>
          )}
        </div>
        <Link
          to="/play"
          className="rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-emerald-900 hover:bg-white/90"
        >
          Play
        </Link>
      </header>

      {/* Global / Country toggle. Hidden when the user hasn't picked a
          country yet (XX) — there's nothing to filter to. */}
      {showCountryToggle && !needsSignIn && (
        <div className="flex gap-1 self-start rounded-full bg-white/5 p-1 text-xs">
          <button
            type="button"
            className={`rounded-full px-3 py-1 font-semibold transition ${
              filterCountry === null
                ? "bg-white text-emerald-900"
                : "opacity-70 hover:opacity-100"
            }`}
            onClick={() => setFilterCountry(null)}
          >
            Global
          </button>
          <button
            type="button"
            className={`flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold transition ${
              filterCountry === userCountry
                ? "bg-white text-emerald-900"
                : "opacity-70 hover:opacity-100"
            }`}
            onClick={() => setFilterCountry(userCountry)}
          >
            {userCountry && (
              <img
                src={flagUrl(userCountry, 20) ?? ""}
                width={14}
                height={10}
                alt=""
                className="rounded-[2px]"
              />
            )}
            {resolveCountry(userCountry ?? "XX").name}
          </button>
        </div>
      )}

      {needsSignIn && (
        <div className="rounded-2xl bg-white/5 p-6 text-center">
          <p className="text-base font-medium">Sign in to see today’s leaderboard.</p>
          <p className="mt-1 text-xs opacity-70">
            Anonymous players can take their kick — ranking is for signed-in
            accounts so streaks and country flags travel with you.
          </p>
          <Link
            to="/play"
            className="mt-4 inline-block rounded-full bg-white px-5 py-2 text-sm font-semibold text-emerald-900 hover:bg-white/90"
          >
            Back to today’s kick
          </Link>
        </div>
      )}

      {error && <p className="text-sm text-rose-300">Could not load leaderboard: {error}</p>}

      {!data && !error && !needsSignIn && <p className="opacity-70">Loading&hellip;</p>}

      {data && rows.length === 0 && (
        <p className="rounded-xl bg-white/5 p-6 text-center text-sm opacity-70">
          {filterCountry
            ? `No-one from ${resolveCountry(filterCountry).name} has played today's puzzle yet. Be first!`
            : "No-one has played today's puzzle yet. Be first!"}
        </p>
      )}

      {data && rows.length > 0 && (
        <div className="flex flex-col gap-1 rounded-2xl bg-white/5 p-2">
          {rows.map((r) => (
            <Row key={r.userId + r.rank} r={r} />
          ))}
        </div>
      )}

      {/* Load more — only when the server says there's another page. */}
      {data && data.hasMore && (
        <button
          type="button"
          disabled={loadingMore}
          className="mx-auto mt-1 rounded-full bg-white/10 px-5 py-2 text-sm font-semibold transition hover:bg-white/20 disabled:opacity-50"
          onClick={loadMore}
        >
          {loadingMore ? "Loading…" : `Show next ${PAGE_SIZE}`}
        </button>
      )}

      {/* Pin the current user's row at the bottom if they're not in the
          fetched rows. Honours the country filter on the server side. */}
      {data && data.me && !rows.some((r) => r.isMe) && (
        <>
          <p className="text-center text-xs opacity-50">&hellip;</p>
          <div className="rounded-2xl bg-white/5 p-2">
            <Row r={data.me} />
          </div>
        </>
      )}

      {data && !data.me && !needsSignIn && (
        <p className="rounded-xl bg-white/5 p-3 text-center text-xs opacity-70">
          {filterCountry && filterCountry !== userCountry ? (
            <>You don&rsquo;t appear on this country&rsquo;s leaderboard.</>
          ) : (
            <>
              You haven&rsquo;t played today yet.{" "}
              <Link to="/play" className="underline">Take your shot</Link>.
            </>
          )}
        </p>
      )}
    </main>
  );
}
